/*
  PDP buy area (KARA-69 / KARA-70).

  Two jobs, both forced by how assets/product-info.js (minified, no source) behaves on an
  option change. It does NOT re-render the buy area. handleUpdateProductInfo:
    - replaces <variant-selects> wholesale, so the size radios are destroyed and rebuilt;
    - replaces only the ids Price, Sku, Inventory, Volume, Price-Per-Item, CartBar,
      Preorder, Badges, Quantity-Form;
    - syncs input[name="id"] in #product-form-{section} and #product-form-installment-{section}
      ONLY — those two ids are hardcoded, so our express form is not covered;
    - publishes theme.PUB_SUB_EVENTS.variantChange ('variant-change', theme.dev.js:45) with
      {data: {sectionId, html, variant}}, where variant is null when the option combination
      resolves to nothing.

  So: sync the express form's variant id from that event. window.subscribe is a global
  (theme.dev.js:76-77) and theme.js is deferred in head.liquid:755, so it exists by the
  time this deferred script runs.
*/
(() => {
  const EXPRESS_FORM = '[data-express-form]';
  const EXPRESS_ID = '[data-express-variant-id]';
  const EXPRESS_BUTTON = '[data-express-checkout]';
  const BUY_AREA = '[data-buy-area]';
  const PURCHASE_ACTIONS = '[data-purchase-actions]';

  // The express button is meaningless without a variant to buy: no variant resolved, or a
  // variant that is not available, must not offer a one-click path to checkout.
  const syncExpressForm = (variant) => {
    const form = document.querySelector(EXPRESS_FORM);
    if (!form) return;

    const idInput = form.querySelector(EXPRESS_ID);
    const button = form.querySelector(EXPRESS_BUTTON);
    const buyable = Boolean(variant && variant.available);

    if (idInput) idInput.value = buyable ? variant.id : '';
    if (button) button.disabled = !buyable;
  };

  /*
    variant is the resolved variant from the variant-change payload, or null when the option
    combination resolves to nothing. Sold out means "a real variant that is not available" —
    an unresolvable combination is not something Klaviyo can restock.

    Only the express form and the shipping line hide. ADD TO CART is left alone: the theme
    already disables it and relabels it SOLD OUT via product-info.js's toggleSubmitButton.
    Klaviyo's back-in-stock trigger is left alone too — the widget injects it itself
    (replace_anchor: true) and it is the shopper's only affordance on a sold-out size.

    The [hidden] property needs help from CSS: .product__shipping-summary sets
    display: flex, which beats the UA sheet's [hidden] { display: none }. Task 1 ships
    .product__block--buttons [data-purchase-actions][hidden] { display: none } for exactly
    this. If a sold-out size hides the express button but not the shipping line, that rule
    is missing — do not "fix" it here with inline styles.
  */
  const applyBuyState = (variant) => {
    const buyArea = document.querySelector(BUY_AREA);
    if (!buyArea) return;

    const soldOut = Boolean(variant && variant.available === false);

    buyArea.querySelectorAll(PURCHASE_ACTIONS).forEach((element) => {
      element.hidden = soldOut;
    });
  };

  /*
    Initial load publishes no variant-change, so the state has to come from the DOM.
    product.liquid:327 does put `variant--soldout` on [data-form-wrapper] server-side, but
    product-info.js only maintains that class when settings.show_newsletter is on, and it is
    off (settings_data.json:54) — so it is correct on first paint and stale forever after.
    Reading the variant JSON directly avoids depending on a class nobody is maintaining.
  */
  const initialVariant = () => {
    const selected = document.querySelector('variant-selects [data-selected-variant]');
    if (!selected) return null;

    try {
      return JSON.parse(selected.innerHTML);
    } catch (error) {
      console.warn('Could not read the selected variant:', error);
      return null;
    }
  };

  const init = () => {
    if (!document.querySelector(EXPRESS_FORM)) return;
    if (typeof window.subscribe !== 'function' || !window.theme?.PUB_SUB_EVENTS) return;

    window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, ({data}) => {
      syncExpressForm(data.variant);
      applyBuyState(data.variant);
    });

    // No variant-change fires on load, so a page opened on a sold-out size would sit at a
    // dead disabled button with a live express checkout and a shipping promise beside it.
    applyBuyState(initialVariant());
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, {once: true});
  } else {
    init();
  }
})();
